import React from 'react';
import {
  Box,
  Breadcrumbs,
  Typography,
  Link as MuiLink,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  IconButton,
  Skeleton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useProjectStore } from '../../store/projectStore';
import { useLineStore } from '../../store/lineStore';
import { useAuthStore } from '../../store/authStore';
import { LineFormDialog } from '../../components/LineFormDialog';
import { DeleteConfirmationDialog } from '../../components/DeleteConfirmationDialog';
import { Line, LineCreate, LineUpdate } from '../../types/line';
import { isAdmin, isIssuer } from '../../utils/permissions';

type LineOrderBy = 'name' | 'description' | 'status';

export default function ProjectLines() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore(); // Get the current user from auth store
  const {
    currentProject,
    isLoading: projectLoading,
    error: projectError,
    fetchProject,
  } = useProjectStore();
  const {
    lines,
    isLoading: linesLoading,
    error: linesError,
    fetchLines,
    createLine,
    updateLine,
    deleteLine,
  } = useLineStore();

  const [isLineFormOpen, setIsLineFormOpen] = React.useState(false);
  const [selectedLine, setSelectedLine] = React.useState<Line | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const cellBorder = {
    border: '1px solid #e8e8e8',
  };

  const [searchQuery, setSearchQuery] = React.useState('');
  const [orderBy, setOrderBy] = React.useState<LineOrderBy>('name');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('asc');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    if (id) {
      fetchProject(Number(id));
      fetchLines({ project_id: Number(id) });
    }
  }, [id, fetchProject, fetchLines]);

  const handleCreateLineClick = () => {
    setSelectedLine(undefined);
    setIsLineFormOpen(true);
  };

  const handleEditLineClick = (line: Line) => {
    setSelectedLine(line);
    setIsLineFormOpen(true);
  };

  const handleDeleteLineClick = (line: Line) => {
    setSelectedLine(line);
    setIsDeleteDialogOpen(true);
  };

  const handleLineFormClose = () => {
    setIsLineFormOpen(false);
    setSelectedLine(undefined);
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
    setSelectedLine(undefined);
  };

  const handleLineFormSubmit = async (formData: Partial<Line>) => {
    try {
      if (selectedLine) {
        await updateLine(selectedLine.id, formData as LineUpdate);
      } else if (id) {
        const lineData: LineCreate = {
          name: formData.name!,
          description: formData.description,
          status: formData.status ?? true,
          project_id: Number(id)
        };
        await createLine(lineData);
      }
      handleLineFormClose();
      fetchLines({ project_id: Number(id) });
    } catch (error) {
      // Error will be handled by the store
    }
  };

  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredLines = React.useMemo(() => (
    lines
      .filter((line) => {
        if (!normalizedSearch) return true;
        const name = line.name?.toLowerCase() || '';
        const description = line.description?.toLowerCase() || '';
        return name.includes(normalizedSearch) || description.includes(normalizedSearch);
      })
  ), [lines, normalizedSearch]);

  const sortedLines = React.useMemo(() => {
    const stabilized = filteredLines.map((line, index) => ({ line, index }));

    stabilized.sort((a, b) => {
      let comparator = 0;
      if (orderBy === 'status') {
        comparator = Number(a.line.status) - Number(b.line.status);
      } else {
        const valueA = (a.line[orderBy] || '').toString().toLowerCase();
        const valueB = (b.line[orderBy] || '').toString().toLowerCase();
        comparator = valueA.localeCompare(valueB);
      }
      if (comparator === 0) {
        return a.index - b.index;
      }
      return order === 'asc' ? comparator : -comparator;
    });

    return stabilized.map((item) => item.line);
  }, [filteredLines, orderBy, order]);

  const paginatedLines = React.useMemo(
    () => sortedLines.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedLines, page, rowsPerPage]
  );

  const handleRequestSort = (_event: React.MouseEvent<unknown>, property: LineOrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, rowsPerPage]);

  if (projectLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (!currentProject) {
    return <Typography color="error">Project not found</Typography>;
  }

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink component={Link} to="/projects" color="inherit">
            Events
          </MuiLink>
          <MuiLink component={Link} to={`/projects/${id}`} color="inherit">
            {currentProject.name}
          </MuiLink>
          <Typography color="text.primary">Lines</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">Lines</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search lines..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={handleCreateLineClick}
            >
              Add Line
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 3 }}>
          {linesLoading ? (
            <Skeleton variant="rectangular" height={200} />
          ) : linesError ? (
            <Typography color="error">{linesError}</Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
                  <TableRow>
                    <TableCell sx={cellBorder} sortDirection={orderBy === 'name' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={cellBorder} sortDirection={orderBy === 'description' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'description'}
                        direction={orderBy === 'description' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'description')}
                      >
                        Description
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={cellBorder} sortDirection={orderBy === 'status' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={(event) => handleRequestSort(event, 'status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={cellBorder}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedLines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell sx={cellBorder}>{line.name}</TableCell>
                      <TableCell sx={cellBorder}>{line.description || '-'}</TableCell>
                      <TableCell sx={cellBorder}>
                        <Chip
                          label={line.status ? 'Active' : 'Inactive'}
                          color={line.status ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" sx={cellBorder}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {(isAdmin(user) || isIssuer(user)) && (
                            <IconButton
                              size="small"
                              onClick={() => handleEditLineClick(line)}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          {(isAdmin(user) || isIssuer(user)) && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteLineClick(line)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!paginatedLines.length && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ ...cellBorder, py: 4 }}>
                        {linesLoading ? 'Loading lines...' : 'No lines found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={sortedLines.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Dialogs */}
      <LineFormDialog
        open={isLineFormOpen}
        onClose={handleLineFormClose}
        onSubmit={handleLineFormSubmit}
        projectId={Number(id)}
        line={selectedLine}
      />
      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteDialogClose}
        onConfirm={async () => {
          if (selectedLine) {
            await deleteLine(selectedLine.id);
            handleDeleteDialogClose();
            fetchLines({ project_id: Number(id) });
          }
        }}
        title="Delete Line"
        message="Are you sure you want to delete this line? This action cannot be undone."
      />
    </>
  );
}
